import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { propertyAPI, reservationAPI } from '@/services/api';
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, Building } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [products, setProducts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchProducts();
      fetchReservations();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      if (response.data.length > 0) {
        
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/inventory/products?property_id=${selectedProperty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await reservationAPI.getAll(selectedProperty);
      // Only show active reservations (checked_in or confirmada)
      const activeRes = response.data.filter(r => ['checked_in', 'confirmada'].includes(r.estado));
      setReservations(activeRes);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addToCart = (product) => {
    if (product.stock_actual <= 0) {
      toast.error('Producto sin stock');
      return;
    }

    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      if (existing.cantidad >= product.stock_actual) {
        toast.error('No hay suficiente stock');
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        nombre: product.nombre,
        cantidad: 1,
        precio: product.precio_venta,
        max_stock: product.stock_actual
      }]);
    }
  };

  const updateCartQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.cantidad + delta;
        if (newQty <= 0) return null;
        if (newQty > item.max_stock) {
          toast.error('Stock insuficiente');
          return item;
        }
        return { ...item, cantidad: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.cantidad * item.precio), 0);
  };

  const handleSale = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    if (paymentMethod === 'cargo_habitacion' && !selectedReservation) {
      toast.error('Selecciona una habitación para cargar');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/sales`,
        {
          property_id: selectedProperty,
          items: cart.map(item => ({
            product_id: item.product_id,
            cantidad: item.cantidad,
            precio: item.precio
          })),
          metodo_pago: paymentMethod,
          reservation_id: paymentMethod === 'cargo_habitacion' ? selectedReservation : null,
          total: getTotal(),
          notas: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Venta registrada exitosamente');
      setCart([]);
      setSelectedReservation(null);
      fetchProducts();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
              Ventas Caja
            </h1>
            <p className="text-muted-foreground mt-1">
              Punto de venta y cargos a habitación
            </p>
          </div>

          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-full sm:w-[240px]">
                <SelectValue placeholder="Selecciona propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>{prop.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Products */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Productos Disponibles</CardTitle>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No hay productos en el inventario
                  </p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-primary hover:shadow-md ${
                          product.stock_actual <= 0 ? 'opacity-50' : ''
                        }`}
                        onClick={() => addToCart(product)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{product.nombre}</h4>
                            <p className="text-sm text-muted-foreground">{product.categoria}</p>
                          </div>
                          <Badge variant={product.stock_actual <= 5 ? 'destructive' : 'secondary'}>
                            {product.stock_actual} disp.
                          </Badge>
                        </div>
                        <p className="text-lg font-bold mt-2 text-primary">
                          ${product.precio_venta.toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cart */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <ShoppingCart className="h-5 w-5" />
                  Carrito
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    Carrito vacío
                  </p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.product_id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.nombre}</p>
                          <p className="text-xs text-muted-foreground">
                            ${item.precio.toLocaleString()} x {item.cantidad}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => updateCartQuantity(item.product_id, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center font-medium">{item.cantidad}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => updateCartQuantity(item.product_id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500"
                            onClick={() => removeFromCart(item.product_id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Total</span>
                    <span className="text-primary">${getTotal().toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card>
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Método de Pago</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant={paymentMethod === 'efectivo' ? 'default' : 'outline'}
                    className="flex flex-col gap-1 h-auto py-3"
                    onClick={() => setPaymentMethod('efectivo')}
                  >
                    <Banknote className="h-5 w-5" />
                    <span className="text-xs">Efectivo</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'tarjeta' ? 'default' : 'outline'}
                    className="flex flex-col gap-1 h-auto py-3"
                    onClick={() => setPaymentMethod('tarjeta')}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="text-xs">Tarjeta</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'cargo_habitacion' ? 'default' : 'outline'}
                    className="flex flex-col gap-1 h-auto py-3"
                    onClick={() => setPaymentMethod('cargo_habitacion')}
                  >
                    <Building className="h-5 w-5" />
                    <span className="text-xs">Habitación</span>
                  </Button>
                </div>

                {paymentMethod === 'cargo_habitacion' && (
                  <div className="space-y-2">
                    <Label>Seleccionar Habitación</Label>
                    <Select value={selectedReservation} onValueChange={setSelectedReservation}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona reserva" />
                      </SelectTrigger>
                      <SelectContent>
                        {reservations.map((res) => (
                          <SelectItem key={res.id} value={res.id}>
                            {res.guest_name} - {res.estado}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  className="w-full" 
                  size="lg"
                  disabled={cart.length === 0}
                  onClick={handleSale}
                  data-testid="complete-sale-btn"
                >
                  Completar Venta
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
